import React, { useEffect, useRef, useState } from 'react';
import { setAuthToken } from '../../lib/auth';

type Presence = 'online' | 'idle' | 'dnd' | 'invisible';

export default function UserQuickSettings({
  open,
  name,
  username,
  avatarUrl,
  presence,
  onClose,
  onOpenFriends,
  onOpenSettings,
  onEditProfile,
  onOpenProfile,
  onOpenNotifications,
  onChangePresence,
  onMarkAllRead,
  friendRequestCount = 0,
  notificationCount = 0,
  statusText,
  onUpdateStatus,
}: {
  open: boolean;
  name: string;
  username?: string;
  avatarUrl?: string | null;
  presence: Presence;
  onClose: () => void;
  onOpenFriends: () => void;
  onOpenSettings: () => void;
  onEditProfile: () => void;
  onOpenProfile: () => void;
  onOpenNotifications: () => void;
  onChangePresence: (p: Presence) => void;
  onMarkAllRead?: () => void;
  friendRequestCount?: number;
  notificationCount?: number;
  statusText: string;
  onUpdateStatus: (next: string) => Promise<void> | void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [activityDraft, setActivityDraft] = useState(statusText || '');
  const [activitySaving, setActivitySaving] = useState(false);
  const presenceOptions: { id: Presence; label: string; color: string }[] = [
    { id: 'online', label: 'Online', color: 'bg-online' },
    { id: 'idle', label: 'Idle', color: 'bg-amber-500' },
    { id: 'dnd', label: 'DND', color: 'bg-red-500' },
    { id: 'invisible', label: 'Invisible', color: 'bg-neutral-500' },
  ];

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      const t = e.target as Node | null;
      if (!ref.current || !t) return;
      if (!ref.current.contains(t)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  useEffect(() => {
    setActivityDraft(statusText || '');
  }, [statusText]);

  if (!open) return null;
  const hasActivityChanges = activityDraft !== (statusText || '');

  const Item = ({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) => (
    <button onClick={onClick} className="w-full text-left px-3 py-2 rounded-md bg-neutral-900 hover:bg-neutral-800 border border-neutral-800 text-fg flex items-center justify-between">
      {children}
    </button>
  );

  const Dot = ({ color }: { color: string }) => (
    <span className={`inline-block h-2.5 w-2.5 rounded-full ${color}`} />
  );

  return (
    <div ref={ref} className="fixed bottom-6 left-4 md:left-[5rem] z-[6500] w-72 max-w-[92vw] select-none">
      <div className="rounded-xl overflow-hidden border border-neutral-800 bg-neutral-950 shadow-2xl">
        <div className="p-3 bg-neutral-900 border-b border-neutral-800 flex items-center gap-3">
          {avatarUrl && !avatarBroken ? (
            <img
              src={avatarUrl}
              alt={name || 'me'}
              className="h-10 w-10 rounded-full object-cover bg-neutral-800 border border-neutral-700"
              onError={() => setAvatarBroken(true)}
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-neutral-800 border border-neutral-700 flex items-center justify-center text-sm font-medium text-fg-2 shrink-0">
              {(name || username || '?').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-fg font-medium truncate">{name || 'Me'}</div>
            {username ? <div className="text-xs text-fg-2 truncate">{username}</div> : null}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button aria-label="Open notifications" onClick={() => { onClose(); onOpenNotifications(); }} className="relative text-fg-2 hover:text-fg">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              {notificationCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-amber-400 text-black border border-neutral-900 text-meta flex items-center justify-center">
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </button>
            <button aria-label="Open friends" onClick={() => { onClose(); onOpenFriends(); }} className="relative text-fg-2 hover:text-fg">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              {friendRequestCount > 0 && (
                <span className="absolute -top-2 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-emerald-600 text-emerald-50 border border-neutral-900 text-meta flex items-center justify-center">
                  {friendRequestCount > 99 ? '99+' : friendRequestCount}
                </span>
              )}
            </button>
            <button aria-label="Open settings" onClick={onOpenSettings} className="text-fg-2 hover:text-fg">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V22a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 8 20.17a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 3 15.4 1.65 1.65 0 0 0 1.5 14H1.41a2 2 0 1 1 0-4H1.5A1.65 1.65 0 0 0 3 8.6 1.65 1.65 0 0 0 2.17 6.77l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 8 3.83 1.65 1.65 0 0 0 9.5 2.5V2.41a2 2 0 1 1 4 0V2.5A1.65 1.65 0 0 0 15.4 3a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 21 8.6c.36.5.57 1.11.5 1.77H21.5a2 2 0 1 1 0 4H21.5A1.65 1.65 0 0 0 19.4 15z"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="p-2 space-y-0">
          {/* Activity status */}
          <div className="px-1 pt-1 pb-2">
            <div className="flex items-center justify-between text-meta font-medium text-fg-3 mb-1.5">
              <span>Activity</span>
              <span>{activityDraft.length}/160</span>
            </div>
            <textarea
              className="w-full rounded-md border border-neutral-800 bg-neutral-900/80 px-2 py-1.5 text-sm text-fg placeholder-neutral-500 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              maxLength={160}
              rows={2}
              placeholder="Share what you're up to..."
              aria-label="Activity"
              value={activityDraft}
              onChange={(e) => setActivityDraft(e.target.value)}
            />
            <div className="mt-1.5 flex justify-end gap-2 text-xs">
              <button
                className="px-2 py-1 rounded-md border border-neutral-800 text-fg-2 hover:text-fg disabled:opacity-40"
                onClick={() => setActivityDraft(statusText || '')}
                disabled={!hasActivityChanges || activitySaving}
              >
                Reset
              </button>
              <button
                className="px-3 py-1 rounded-md border border-brand/60 text-brand hover:bg-brand/20 disabled:opacity-40"
                disabled={!hasActivityChanges || activitySaving}
                onClick={async () => {
                  if (!hasActivityChanges || activitySaving) return;
                  setActivitySaving(true);
                  try {
                    await onUpdateStatus(activityDraft);
                  } finally {
                    setActivitySaving(false);
                  }
                }}
              >
                {activitySaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-neutral-800/80 mx-1" />

          {/* Profile actions */}
          <div className="py-1 space-y-0.5">
            <Item
              onClick={() => {
                onClose();
                onOpenProfile();
              }}
            >
              <span className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                  <path d="M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                  <path d="M5 21v-1a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v1" />
                </svg>
                View My Profile
              </span>
            </Item>
            <Item
              onClick={() => {
                onClose();
                onEditProfile();
              }}
            >
              <span className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M4 21v-7a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v7"/><circle cx="12" cy="7" r="4"/></svg>
                Edit Profile
              </span>
            </Item>
            {onMarkAllRead && (
              <Item onClick={() => { onMarkAllRead(); }}>
                <span className="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M18 6 7 17l-5-5"/><path d="m22 10-7.5 7.5L13 16"/></svg>
                  Mark all as read
                </span>
              </Item>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-neutral-800/80 mx-1" />

          {/* Presence */}
          <div className="px-1 pt-2 pb-1">
            <div className="flex items-center justify-between text-meta font-medium text-fg-3 mb-1.5">
              <span>Presence</span>
              <span className="text-fg-2 normal-case tracking-normal">{presenceOptions.find((opt) => opt.id === presence)?.label || ''}</span>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {presenceOptions.map((opt) => {
                const active = presence === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => onChangePresence(opt.id)}
                    className={`flex items-center gap-1.5 rounded-lg border text-xs px-2 py-1.5 transition ${active ? 'border-brand bg-brand/10 text-brand' : 'border-neutral-800 bg-neutral-900 text-fg-2 hover:border-neutral-700'}`}
                  >
                    <Dot color={opt.color} />
                    <span className="truncate">{opt.label}</span>
                    {active && <span className="ml-auto text-brand text-meta">Active</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-neutral-800/80 mx-1" />

          {/* Log out */}
          <div className="py-1">
            <button
              className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-md text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
              onClick={async () => {
                try { await fetch('/api/auth/logout', { method: 'POST' }); } catch {}
                setAuthToken(null);
                try { localStorage.removeItem('user'); localStorage.removeItem('me'); localStorage.removeItem('profile.activity'); } catch {}
                location.reload();
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 shrink-0">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Log out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
